// Routes

"use strict";

import { createRouter, createWebHistory, Router, RouteRecordRaw } from "vue-router";
import { Timeouts } from "./utils/timeout";
import { AppEvents } from "./control/app-events";

// Router
// https://router.vuejs.org/guide/#javascript

// List of forbidden routes:
//
//    - /api/*  - This is reserved for the API
//    - /static/* - This is reserved for static assets
//    - /webhooks*/ - Reserved for webhooks

const routes: (RouteRecordRaw & {
    meta?: {
        /**
         * Set it to true for sticky sidebar
         */
        sidebarSticky?: boolean;
    };
})[] = [
    /* General / Home */

    {
        name: "home",
        path: "/",
        component: () => import("@/components/routes/HomePage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Home'
        },
    },

    {
        name: "users",
        path: "/users",
        component: () => import("@/components/routes/users/UsersPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Users'
        },
    },

    {
        name: "forms",
        path: "/forms",
        component: () => import("@/components/routes/forms/FormsPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Forms'
        },
    },

    {
        name: "form-page",
        path: "/forms/:formName",
        component: () => import("@/components/routes/forms/FormPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Form'
        },
    },

    {
        name: "form-page-id",
        path: "/forms/by-id/:formId",
        component: () => import("@/components/routes/forms/FormPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Form'
        },
    },

    {
        name: "answer-forms",
        path: "/answer-forms",
        component: () => import("@/components/routes/forms/AnswerFormsPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Answer forms'
        },
    },

    {
        name: "answer-form-page",
        path: "/answer-forms/:formName",
        component: () => import("@/components/routes/forms/AnswerFormPage.vue"),
        props: true,
         meta: {
            sidebarSticky: true,
            title: 'Answer form'
        },
    },

    {
        name: "answer-form-page-read-only",
        path: "/patients/:patientId/forms/:answerGroupId",
        component: () => import("@/components/routes/forms/AnswerFormPageReadOnly.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Form'
        },
    },

    {
        name: "my-medical-data",
        path: "/my-medical-data",
        component: () => import("@/components/routes/patients/patient/MyMedicalDataPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'My medical data'
        },
    },

    {
        name: "patient-diagnostics",
        path: "/my-medical-data/patient-diagnostics/:id",
        component: () => import("@/components/routes/patients/patient/PatientDiagnostics.vue"),
         meta: {
            sidebarSticky: true,
            title: 'Associated diagnostics'
        },
    },

    {
        name: "patient-dicoms",
        path: "/my-medical-data/patient-dicoms/:id",
        component: () => import("@/components/routes/patients/patient/PatientDICOMS.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Associated DICOM images'
        },
    },

    {
        name: "patient-form-diagnostics",
        path: "/my-medical-data/patient-form-diagnostics/:id",
        component: () => import("@/components/routes/patients/patient/PatientFormDiagnostics.vue"),
        meta: {
            sidebarSticky: true,
            title: 'My medical form diagnostics'
        },
    },
    
    {
        name: "assigned-patients",
        path: "/assigned-patients",
        component: () => import("@/components/routes/patients/AssignedPatientsPage.vue"),
         meta: {
            sidebarSticky: true,
            title: 'Assigned patients'
        },
    },
    {
        name: "patient-information",
        path: "/users/:patientid",
        component: () => import("@/components/routes/patients/PatientInformationPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Detailed information'
        },
    },
    {
        name: "patient-forms",
        path: "/patients/:patientId/forms",
        component: () => import("@/components/routes/patients/PatientFormsPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Forms'
        },
    },
    {
        name: "patient-stats",
        path: "/patients/:patientId/stats",
        component: () => import("@/components/routes/account/AccountChartsPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Statistics'
        },
    },
    {
        name: "assigned-workers",
        path: "/assigned-workers",
        component: () => import("@/components/routes/patients/AssignedWorkersPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Assigned workers'
        }
    },
    {
        name: "diagnostic-information",
        path: "/patients/:patientid/diagnostics/:diagnosticid",
        component: () => import("@/components/routes/diagnostics/DiagnosticInformationPage.vue"),
        props:true,
        meta: {
            sidebarSticky: true,
            title: 'Diagnostic Details'
        }
    },
    {
        name: "scoring-systems",
        path: "/forms/scoring-systems",
        component: () => import("@/components/routes/forms/ScoringSystemsPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Scoring systems'
        }
    },
    {
        name: "dicom-information",
        path: "/dicom/:idimages/patient/:patientid",
        component: () => import("@/components/routes/dicoms/DicomInformationPage.vue"),
        props:true,
        meta: {
            sidebarSticky: true,
            title: 'DICOM'
        }
    },
    {
        name: "formhl7-information",
        path: "/patients/:patientid/formhl7/:bundleid",
        component: () => import("@/components/routes/forms/FormHl7InformationPage.vue"),
        props:true,
        meta: {
            sidebarSticky: true,
            title: 'Form Diagnostic Details'
        }
    },

    {
        name: "satisfaction-questionnaires",
        path: "/satisfaction-questionnaires",
        component: () => import("@/components/routes/questionnaires/SatisfactionQuestionnairesPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Satisfaction Questionnaires'
        },
    },

    {
        name: "satisfaction-questionnaire",
        path: "/satisfaction-questionnaire/:name",
        component: () => import("@/components/routes/questionnaires/SatisfactionQuestionnaire.vue"),
        props: true,
         meta: {
            sidebarSticky: true,
            title: 'Satisfaction Questionnaire'
        },
    },

    {
        name: "questionnaires-statistics",
        path: "/questionnaires-statistics",
        component: () => import("@/components/routes/questionnaires/StatisticsSatisfactionQuestionnairesPage.vue"),
        props: true,
         meta: {
            sidebarSticky: true,
            title: 'Questionnaires statistics'
        },
    },

    {
        name: "questionnaire-statistics",
        path: "/questionnaire-statistics/:name",
        component: () => import("@/components/routes/questionnaires/StatisticsSatisfactionQuestionnaire.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
            title: 'Questionnaire statistics'
        },
    },


    /* Auth */

    {
        name: "login",
        path: "/login",
        component: () => import("@/components/routes/auth/LoginPage.vue"),
        meta: {
            title: 'Login'
        }
    },

    {
        name: "tfa-login",
        path: "/login/tfa",
        component: () => import("@/components/routes/auth/TwoFactorLoginPage.vue"),
        meta: {
            title: 'Login'
        }
    },
    {
        name: "forgot-password",
        path: "/password/forgot",
        component: () => import("@/components/routes/auth/ForgotPasswordPage.vue"),
        meta: {
            title: 'Forgot your password?'
        }
    },
    {
        name: "reset-password",
        path: "/password/reset/:uid/:token",
        component: () => import("@/components/routes/auth/ResetPasswordPage.vue"),
        meta: {
            title: 'Reset password'
        }
    },

    {
        name: "verify-email",
        path: "/email/verify/:uid/:token",
        component: () => import("@/components/routes/auth/EmailVerifyPage.vue"),
        meta: {
            title: 'Email verification'
        }
    },

    {
        name: "tp-login",
        path: "/login/tp/:service",
        component: () => import("@/components/routes/auth/ThirdPartyLogin.vue"),
        meta: {
            title: 'Logging in'
        }
    },

    {
        name: "tp-signup",
        path: "/signup/tp",
        component: () => import("@/components/routes/auth/ThirdPartySignupPage.vue"),
        meta: {
            title: 'Create an account'
        }
    },

    /* Profile */

    {
        name: "profile",
        path: "/user/:username",
        component: () => import("@/components/routes/profile/ProfilePage.vue"),
        meta: {
            title: 'Profile page'
        }
    },

    /* Account */

    {
        name: "account-settings",
        path: "/account-settings",
        component: () => import("@/components/routes/account/AccountSettingsPage.vue"),
        meta: {
            title: 'Account settings'
        }
    },

    {
        name: "account-charts",
        path: "/account-charts",
        component: () => import("@/components/routes/account/AccountChartsPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Statistics'
        },
    },

    /* Wallet */

    {
        name: "wallet",
        path: "/wallet/:id",
        component: () => import("@/components/routes/wallet/WalletSettingsPage.vue"),
        meta: {
            title: 'Wallet'
        },
    },

    /* Admin */

    {
        name: "admin",
        path: "/admin",
        component: () => import("@/components/routes/admin/AdministrationPage.vue"),
        meta: {
            title: 'Administration and moderation'
        },
    },

    {
        name: "admin-user",
        path: "/admin/users/:id",
        component: () => import("@/components/routes/admin/UserPage.vue"),
        meta: {
            title: 'User page'
        },
    },

    /* Assistant */

    {
        path: '/assistant',
        name: 'assistant',
        component: () => import('@/components/routes/assistant/AssistantPage.vue'),
        meta: {
            sidebarSticky: true,
            title: 'Assistant'
        },
    },

    /* Notifications */

    {
        path: '/notifications',
        name: 'notifications',
        component: () => import('@/components/routes/account/NotificationsPanel.vue'),
        meta: {
            sidebarSticky: true,
            title: 'Notifications'
        },
    },

    /* Terms */

    {
        name: "terms",
        path: "/terms",
        component: () => import("@/components/routes/TermsOfUsePage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Terms'
        },
    },

    /* Cookies */

    {
        name: "cookies",
        path: "/cookies",
        component: () => import("@/components/routes/CookiePolicyPage.vue"),
        meta: {
             sidebarSticky: true,
            title: 'Cookies policy'
        },
    },

    /* Privacy */
    
    {
        name: "privacy",
        path: "/privacy",
        component: () => import("@/components/routes/PrivacyPolicyPage.vue"),
        meta: {
            sidebarSticky: true,
            title: 'Privacy policy'
        },
    },

    /* Default */

    {
        name: "not-found",
        path: "/:catchAll(.*)",
        component: () => import("@/components/routes/NotFoundPage.vue"),
        meta: {
            title: 'Not found'
        },
    },
];

export function makeApplicationRouter(): Router {
    const router = createRouter({
        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
        history: createWebHistory(),
        routes, // short for `routes: routes`
    });

    router.beforeEach(() => {
        Timeouts.Set("router-load-state", 300, () => {
            AppEvents.Emit("router-load-state-change", true);
        });
    });

    router.afterEach(() => {
        Timeouts.Abort("router-load-state");
        AppEvents.Emit("router-load-state-change", false);
    });

    return router;
}
